import { useEffect, useRef, useState } from 'react';
import {
    BROWSE_CHROME_COMPACT_COOLDOWN_MS,
    BROWSE_CHROME_COMPACT_OFF_PX,
    BROWSE_CHROME_COMPACT_ON_PX,
    isBrowseChromeCompactAllowed,
    resolveBrowseChromeCompact,
} from './browseChromeCompactLogic';
import {
    isBrowseChromeMorphLocked,
    lockBrowseChromeMorph,
    stageBrowseChromeMorphAnchor,
    unlockBrowseChromeMorph,
} from './browseChromeMorphUtils';

const SCROLLING_CLASS = 'browse-chrome-scrolling';
const MORPHING_CLASS = 'browse-chrome-morphing';
const COMPACT_CLASS = 'browse-chrome-compact';
const SCROLLING_IDLE_MS = 120;
const SCROLL_DIRECTION_EPS_PX = 2;
/** Compact enter waits for scroll idle so a single wheel notch is not fought by chrome collapse */
const COMPACT_ENTER_IDLE_MS = 140;

/**
 * True when the user has scrolled down on a browse page — drives compact header,
 * navbar (hamburger drawer on desktop via Navbar), and filter chrome
 * via `html.browse-chrome-compact`. Disabled on mobile (≤768px).
 */
export function useBrowseChromeCompact(enabled) {
    const [compact, setCompact] = useState(false);
    const compactRef = useRef(false);
    const cooldownUntilRef = useRef(0);
    const lastScrollYRef = useRef(0);
    const rafRef = useRef(null);
    const scrollingIdleRef = useRef(null);
    const compactEnterIdleRef = useRef(null);
    const compactAllowedRef = useRef(true);

    useEffect(() => {
        if (!enabled) {
            compactRef.current = false;
            cooldownUntilRef.current = 0;
            unlockBrowseChromeMorph();
            setCompact(false);
            return undefined;
        }

        const root = document.documentElement;
        lastScrollYRef.current = window.scrollY || document.documentElement.scrollTop || 0;

        const syncCompactAllowed = () => {
            compactAllowedRef.current = isBrowseChromeCompactAllowed(window.innerWidth);
            if (!compactAllowedRef.current && compactRef.current) {
                compactRef.current = false;
                cooldownUntilRef.current = 0;
                unlockBrowseChromeMorph();
                setCompact(false);
            }
        };

        const scheduleCompactEnterAfterIdle = () => {
            if (compactEnterIdleRef.current) {
                clearTimeout(compactEnterIdleRef.current);
            }
            compactEnterIdleRef.current = window.setTimeout(() => {
                compactEnterIdleRef.current = null;
                if (!compactAllowedRef.current || compactRef.current) {
                    return;
                }
                const { y, maxScroll } = readScrollMetrics();
                if (y <= BROWSE_CHROME_COMPACT_OFF_PX) {
                    return;
                }
                const target = resolveBrowseChromeCompact(false, y, maxScroll);
                if (target && y > BROWSE_CHROME_COMPACT_ON_PX) {
                    applyCompact(true);
                }
            }, COMPACT_ENTER_IDLE_MS);
        };

        const markScrolling = () => {
            if (!compactAllowedRef.current) {
                return;
            }
            root.classList.add(SCROLLING_CLASS);
            if (scrollingIdleRef.current) {
                clearTimeout(scrollingIdleRef.current);
            }
            scrollingIdleRef.current = window.setTimeout(() => {
                scrollingIdleRef.current = null;
                root.classList.remove(SCROLLING_CLASS);
            }, SCROLLING_IDLE_MS);
            scheduleCompactEnterAfterIdle();
        };

        const readScrollMetrics = () => {
            const doc = document.documentElement;
            const y = Math.max(0, window.scrollY || doc.scrollTop || 0);
            const maxScroll = Math.max(0, doc.scrollHeight - window.innerHeight);
            return { y, maxScroll };
        };

        const applyCompact = (next) => {
            if (next === compactRef.current) {
                return;
            }
            if (isBrowseChromeMorphLocked()) {
                /* Always allow expand at top; block overlapping enter morphs */
                if (next) {
                    return;
                }
                unlockBrowseChromeMorph();
            }
            if (next && Date.now() < cooldownUntilRef.current) {
                return;
            }

            if (!next && compactEnterIdleRef.current) {
                clearTimeout(compactEnterIdleRef.current);
                compactEnterIdleRef.current = null;
            }

            stageBrowseChromeMorphAnchor(next);
            lockBrowseChromeMorph();
            /* Same frame as scroll — avoid one frame of frozen/partial collapse before React layout */
            root.classList.toggle(COMPACT_CLASS, next);
            root.classList.add(MORPHING_CLASS);
            compactRef.current = next;
            cooldownUntilRef.current = Date.now() + BROWSE_CHROME_COMPACT_COOLDOWN_MS;
            setCompact(next);
        };

        const syncWhileScrolling = () => {
            syncCompactAllowed();
            if (!compactAllowedRef.current) {
                return;
            }

            const { y, maxScroll } = readScrollMetrics();
            const prevY = lastScrollYRef.current;
            const scrollingUp = y < prevY - SCROLL_DIRECTION_EPS_PX;
            lastScrollYRef.current = y;

            if (y <= BROWSE_CHROME_COMPACT_OFF_PX) {
                if (compactRef.current && !isBrowseChromeMorphLocked()) {
                    applyCompact(false);
                }
                return;
            }

            const target = resolveBrowseChromeCompact(compactRef.current, y, maxScroll);

            if (scrollingUp && compactRef.current && !target && !isBrowseChromeMorphLocked()) {
                applyCompact(false);
            }
        };

        const onScrollOrResize = () => {
            if (compactAllowedRef.current) {
                markScrolling();
            }
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                syncWhileScrolling();
            });
        };

        syncCompactAllowed();
        syncWhileScrolling();
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('wheel', markScrolling, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
            if (scrollingIdleRef.current) {
                clearTimeout(scrollingIdleRef.current);
            }
            if (compactEnterIdleRef.current) {
                clearTimeout(compactEnterIdleRef.current);
            }
            unlockBrowseChromeMorph();
            root.classList.remove(SCROLLING_CLASS);
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('wheel', markScrolling);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [enabled]);

    return compact;
}
